import React from 'react';
import {
  CircularProgress,
  Divider,
  Fade,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import uafLogo from '../../assets/uaf.svg';
import getPartnerButton from '../../components/PartnerButtons';
import TermsAndConditionsLink from '../../components/TermsAndConditionsLink';
import type { Partner } from '../../components/LeadFormIndex/types';
import { getComplementInviteContext } from '../../queries/complementInvite';
import analytics, { resetAnalytics } from '../../utils/analytics';
import {
  INVITEE_CATEGORY,
  INVITEE_DEAD_LINK_PAGE,
  INVITEE_RATE_LIMIT_PAGE,
  INVITEE_WELCOME_PAGE,
} from '../../feature/preloan/v3/ComplementInvite/analyticsPages';
import {
  COMPLEMENT_DISCLOSURE,
  CREDIT_RESPONSIBILITY_NOTICE,
  INVITEE_CLAVE_UNICA_NOTICE,
  INVITE_DEAD_BODY,
  INVITE_DEAD_TITLE,
  INVITE_RATE_LIMIT_BODY,
  INVITE_RATE_LIMIT_TITLE,
  invitedByLine,
} from '../../feature/preloan/v3/ComplementInvite/copy';
import type { ComplementInviteContext } from '../../types/complementInvite';

type ComplementInviteWelcomeProps = {
  complementToken: string;
  origin: string;
  partner: Partner;
  handleSetShowForm: (show: boolean, leadFirstName?: string) => void;
};

export default function ComplementInviteWelcome({
  complementToken, origin, partner, handleSetShowForm,
}: ComplementInviteWelcomeProps) {
  const theme = useTheme();
  const inviteToken = complementToken;

  const [loading, setLoading] = React.useState(true);
  const [context, setContext] = React.useState<ComplementInviteContext['data'] | null>(null);
  const [checked, setChecked] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [rateLimited, setRateLimited] = React.useState(false);
  const [logo, setLogo] = React.useState<string | null>(null);
  const [footerLogo, setFooterLogo] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    resetAnalytics();
  }, []);

  React.useEffect(() => {
    if (loading) return;
    if (rateLimited) {
      analytics.page(INVITEE_CATEGORY, INVITEE_RATE_LIMIT_PAGE);
      return;
    }
    if (failed || !context || !context.eligible) {
      analytics.page(INVITEE_CATEGORY, INVITEE_DEAD_LINK_PAGE);
      return;
    }
    analytics.page(INVITEE_CATEGORY, INVITEE_WELCOME_PAGE);
  }, [loading, failed, rateLimited, context]);

  React.useEffect(() => {
    setLogo(partner.config.styles?.headerLogo || null);
    setFooterLogo(partner.config.styles?.footerLogo);
  }, [partner.config]);

  React.useEffect(() => {
    if (!inviteToken) { setFailed(true); setLoading(false); return; }
    getComplementInviteContext(inviteToken)
      .then((invite) => setContext(invite.data))
      .catch((e) => {
        if (e === 429) setRateLimited(true);
        else setFailed(true);
      })
      .finally(() => setLoading(false));
  }, [inviteToken]);

  const start = () => handleSetShowForm(true, context?.leadFirstName);
  const handleSetChecked = () => setChecked((o) => !o);

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
        <CircularProgress />
      </div>
    );
  }

  const unusable = failed || rateLimited || !context || !context.eligible;
  const boxBackground = partner.config.styles?.backgroundSecondaryColor
    || theme.palette.background.defaultSecondary;

  return (
    <Fade in>
      <div style={{
        backgroundColor: theme.palette.background.default,
        height: '100%',
        display: 'grid',
        alignItems: 'center',
      }}
      >
        <div style={{
          textAlign: 'center',
          margin: 'auto',
          maxWidth: 800,
          width: '100%',
        }}
        >
          {logo ? (
            <div style={{ width: '300px', height: '80px', margin: 'auto' }}>
              <object
                data={logo}
                aria-labelledby="Partner Logo"
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          ) : (
            <div style={{ width: '300px', height: '80px', margin: 'auto' }} />
          )}
          <Typography
            data-pw="complement-invite-header"
            variant="h3"
            fontWeight="light"
            align="center"
            style={{ marginInline: 'auto', marginBlock: '1%' }}
            component="div"
          >
            {rateLimited && INVITE_RATE_LIMIT_TITLE}
            {!rateLimited && unusable && INVITE_DEAD_TITLE}
            {!unusable && invitedByLine(context!.leadFirstName)}
          </Typography>
          <br />
          <div>
            <Divider />
            <Typography
              variant="body1"
              data-pw="complement-invite-text"
              component="div"
              align="center"
              style={{
                padding: '3%',
                lineHeight: '1.8',
                backgroundColor: boxBackground,
              }}
            >
              {!unusable && (
                <>
                  {CREDIT_RESPONSIBILITY_NOTICE}
                  {' '}
                  <b style={{ color: theme.palette.text.primary }}>
                    {INVITEE_CLAVE_UNICA_NOTICE}
                  </b>
                </>
              )}
              {unusable && (rateLimited ? INVITE_RATE_LIMIT_BODY : INVITE_DEAD_BODY)}
            </Typography>
            <Divider />
            {!unusable && (
              <>
                <br />
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <TermsAndConditionsLink
                    origin={origin}
                    checked={checked}
                    handleSetChecked={handleSetChecked}
                    partner={partner}
                    formControlLabelProps={{
                      backgroundColor: theme.palette.background.defaultSecondary,
                      padding: '10px',
                      borderRadius: 20,
                      margin: '0px 20px',
                    }}
                  />
                </div>
              </>
            )}
          </div>
          {!unusable && (
            <>
              <Typography
                variant="body2"
                align="center"
                style={{ padding: '2.5%', maxWidth: 500, margin: 'auto' }}
              >
                Por favor acepta los términos y condiciones, la política de privacidad y
                haz click en
                {' '}
                <span style={{ fontWeight: 'bold' }}>Comenzar</span>
                {' '}
                para continuar
              </Typography>
              {getPartnerButton({
                partner,
                disabled: !checked,
                onClick: start,
                buttonLabel: 'Comenzar',
                endIcon: (
                  <ArrowForwardIcon style={{
                    color: partner.config.styles?.secondaryColor === theme.palette.secondary.main
                      ? theme.palette.white.main
                      : partner.config.styles?.primaryColor,
                  }}
                  />
                ),
                testingLabel: 'preloan-welcome-button',
              })}
            </>
          )}
          <Typography
            variant="caption"
            component="div"
            align="center"
            style={{ padding: '2.5%', maxWidth: 600, margin: 'auto' }}
          >
            {COMPLEMENT_DISCLOSURE}
          </Typography>
          {footerLogo && (
            <Typography
              variant="body1"
              style={{
                margin: '15px', display: 'flex', placeContent: 'center', color: 'gray',
              }}
              fontStyle="italic"
            >
              {'Alianza con '}
              <object
                style={{ marginLeft: 5 }}
                data={footerLogo}
                width="80px"
                height="22px"
                aria-labelledby="Provider Logo"
              />
            </Typography>
          )}
          <Typography
            variant="body2"
            fontStyle="italic"
            align="center"
            style={{
              padding: '2.5%',
              marginLeft: '1em',
              marginRight: '1em',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            Estamos regulados por la
            <a href="https://www.uaf.cl">
              <object
                style={{ marginLeft: 5, marginTop: '10%' }}
                data={uafLogo}
                width="100px"
                aria-labelledby="Provider Logo"
              />
            </a>
          </Typography>
        </div>
      </div>
    </Fade>
  );
}
